/**
 * ToolbarIdentity — the left corner of a board's toolbar: what am I looking at, in two lines
 * behind one icon.
 *
 * It exists because the corner had become a bare statistic ("12 cameras"), which answers a
 * question nobody asked first. An operator arriving on the page — or flipping between the
 * honeycomb and the floor plan — needs to know WHICH board they're on before they care how big
 * it is, and a number in a corner says nothing about that.
 *
 * So: an icon tile that differs per board (the same symbol its segment carries in the view
 * toggle), a bold headline naming the thing on screen, and a quiet second line for the counts
 * and the interaction hint. Both boards render this with identical geometry, so the bar does not
 * reflow when the view is toggled — the icon and the headline change, nothing moves.
 */
import type { ComponentType, ReactNode } from 'react';

export interface ToolbarIdentityProps {
  /** The board's symbol — same one its segment shows in the view toggle. */
  icon: ComponentType<{ className?: string }>;
  /** Names what is on screen: the board, or the layout it is showing. */
  title: ReactNode;
  /**
   * What the title NAMES, when the name alone doesn't say — "Floor plan" in front of
   * "Ground Floor". A bare proper noun in a toolbar corner is a riddle: the reader has to
   * already know what kind of thing it is. Omit where the title IS the kind.
   */
  kind?: string;
  /** `title` as plain text, for the tooltip when it truncates. */
  titleText?: string;
  /** Counts, state, interaction hint — whatever is true right now. Hidden on phones. */
  detail?: ReactNode;
  className?: string;
}

export default function ToolbarIdentity({
  icon: Icon,
  title,
  kind,
  titleText,
  detail,
  className = '',
}: ToolbarIdentityProps) {
  return (
    <div className={`flex min-w-0 items-center gap-2 sm:gap-2.5 ${className}`}>
      {/* Tinted tile rather than a bare glyph: at 16px an outline icon sitting on the toolbar's
          own surface reads as decoration, and this corner is meant to be a label. */}
      <span className="flex h-8 w-8 flex-none items-center justify-center rounded-lg bg-brand/10 text-brand sm:h-9 sm:w-9">
        <Icon className="h-4 w-4 sm:h-5 sm:w-5" />
      </span>

      <div className="flex min-w-0 flex-col justify-center leading-tight">
        <h2
          title={titleText}
          // `truncate` is the last-resort guard: on its own line a name has to outgrow most of
          // the zone before it clips.
          className="max-w-full truncate text-[15px] tracking-tight text-foreground sm:text-base"
        >
          {kind && <span className="mr-1 font-medium text-muted-foreground">{kind}</span>}
          <span className="font-bold">{title}</span>
        </h2>
        {detail && (
          <span className="hidden max-w-full truncate whitespace-nowrap text-[11px] text-muted-foreground sm:block">
            {detail}
          </span>
        )}
      </div>
    </div>
  );
}
